import config from '../config/config';

/**
 * DV-311 monthly compliance messages (admin side).
 *
 * Every call takes `getToken`, normally `getAccessToken` from useAuth(), so the token is
 * refreshed when it is about to expire instead of being read stale from localStorage.
 * Calls resolve to the envelope's `data` and reject with an Error carrying the backend's
 * `message` (and the HTTP `status`, e.g. 409 when the month already has a message).
 */
const BASE_PATH = '/um/v1/admin/compliance-messages';

const request = async (getToken, path = '', { method = 'GET', body } = {}) => {
  const token = await getToken();
  if (!token) throw new Error('Your session has expired. Please log in again.');

  let res;
  try {
    res = await fetch(`${config.API_BASE_URL}${BASE_PATH}${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new Error('Could not reach the server. Check your connection and try again.');
  }

  // DELETE answers with a null payload; tolerate an empty body too.
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(data.message || `Request failed (${res.status})`);
    error.status = res.status;
    throw error;
  }
  return data.data !== undefined ? data.data : null;
};

const ComplianceMessageService = {
  /** All messages, newest month first. */
  list: (getToken) => request(getToken),
  get: (getToken, id) => request(getToken, `/${id}`),
  /** Creates a DRAFT. payload: { period, titleEn, titleEs, bodyEn, bodyEs, linkUrl } */
  create: (getToken, payload) => request(getToken, '', { method: 'POST', body: payload }),
  /** Full replace of the editable fields. On a PUBLISHED message members see it immediately. */
  update: (getToken, id, payload) => request(getToken, `/${id}`, { method: 'PUT', body: payload }),
  publish: (getToken, id) => request(getToken, `/${id}/publish`, { method: 'POST' }),
  unpublish: (getToken, id) => request(getToken, `/${id}/unpublish`, { method: 'POST' }),
  /** Drafts only; the backend answers 409 for a published message. */
  remove: (getToken, id) => request(getToken, `/${id}`, { method: 'DELETE' }),
};

export default ComplianceMessageService;
